<template>
  <component
    :is="tag"
    class="app-button"
    :class="[classConfig, {'w-full': wide},
             {'link': tag === 'router-link' || 'a'}]"
  >
    <slot>
      <component :is="actionIcon || actionConfig[action].icon" v-if="action" weight="bold" class="text-lg" />
    </slot>
  </component>
</template>

<script setup lang="ts">
import { PhPencilSimple, PhTrashSimple, PhSignIn, PhPlus } from '@phosphor-icons/vue'
import type { Component } from 'vue'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'outlined' | 'link' | 'default'
    action?: 'edit' | 'delete' | 'default' | 'add'
    actionIcon?: Component
    tag?: 'button' | 'router-link' | 'a'
    wide?: boolean
  }>(),
  {
    variant: 'primary',
    tag: 'button',
    wide: false
  }
)

const classConfig = computed(() => {
  if (props.action) {
    return `square-sm p-0 ${actionConfig[props.action].style}`
  } else {
    return `is-${props.variant}`
  }
})

const actionConfig = {
  add: { style: 'action-btn text-black-800 hover:opacity-70', icon: PhPlus },
  edit: { style: 'action-btn text-primary hover:opacity-70', icon: PhPencilSimple },
  delete: { style: 'action-btn text-danger hover:opacity-70', icon: PhTrashSimple },
  default: { style: 'action-btn text-black-800 hover:opacity-70', icon: PhSignIn }
}
</script>

<style lang="scss">
.app-button {
  @apply select-none rounded-lg transition-colors duration-300;
  @apply relative inline-flex items-center justify-center h-48 text-md leading-tight font-bold;

  &:not(.square, .square-sm, .is-link) {
    @apply px-15 min-w-[100px]
  }

  &.is-primary {
    @apply text-white bg-primary hover:bg-blue-4 active:bg-blue-4 active:text-blue-4-400;

    &:disabled {
      @apply cursor-not-allowed bg-white-500;
    }
  }

  &.is-secondary {
    @apply text-blue-4-900 bg-blue-4-400 hover:bg-blue-4-500 active:bg-blue-4-600 active:text-primary;

    &:disabled {
      @apply cursor-not-allowed text-black-400 bg-white-600;
    }
  }

  &.is-outlined {
    @apply text-black-700 bg-white border border-blue-4-400 hover:bg-blue-4-300 hover:text-black-900;
    @apply active:border-blue-4-300 active:bg-blue-4-300 active:text-black-800;

    &:disabled {
      @apply cursor-not-allowed bg-white text-white-400 border-white-400;
    }
  }

  &.is-link {
    @apply h-auto w-auto min-w-0 text-primary hover:text-blue-4-900 active:text-blue-4-900;

    &:disabled {
      @apply cursor-not-allowed text-white-600;
    }
  }

  &.link {
    &.disabled {
      @apply cursor-not-allowed bg-white text-white-400 border-white-400;
    }
  }

  &.action-btn {
    &:disabled {
      @apply cursor-not-allowed opacity-60
    }
  }

  &.square {
    @apply w-48 h-48;
  }

  &.square-sm {
    @apply w-36 h-36;
  }
}
</style>
